import styled from "@emotion/styled";
import { UNGROUPED_DISPLAY_NAME } from "constant";
import { Registry, Group } from "types/Gateways";
import { uniq } from "lodash";
import { ChangeEvent, useEffect, useState } from "react";
import { Button, Form, FormControl, Modal } from "react-bootstrap";
import { isStandaloneGroup } from "utils/gatewayFunctions";

const StyledModal = styled(Modal)`
    .modal-filter .modal-option {
        flex: 0 0 160px;
        margin-right: 0.5rem;
        padding: 0;
    }

    .modal-filter .modal-option .custom-control-label:before,
    .modal-filter .modal-option .custom-control-label:after {
        top: 50%;
        transform: translateY(-50%);
    }
`;

function renameStandaloneGroup(group: Group): Group {
    const copy = { ...group };
    if (isStandaloneGroup(group)) {
        copy.name = UNGROUPED_DISPLAY_NAME;
    }
    return copy;
}

function filterGateways(group: Group, searchTerm: string): Group {
    const copy = { ...group };
    if (searchTerm.length) {
        copy.gateways = group.gateways.filter((gateway) =>
            gateway.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }
    return copy;
}

export type SelectGatewayModalProps = {
    isShow: boolean;
    selectedGWs?: string[];
    initGroups: Group[];
    onClose: Function;
    handleAddGWsToGroup: Function;
};

const SelectGatewayModal = ({
    isShow,
    selectedGWs,
    onClose,
    handleAddGWsToGroup,
    initGroups,
}: SelectGatewayModalProps) => {
    const [groups, setGroups] = useState<Group[]>([]);
    const [gatewayIds, setGatewayIds] = useState<string[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    useEffect(() => {
        setSelectedIds([]);
        setGatewayIds([]);
        setGroups([]);

        const filteredGroups = initGroups
            .filter(
                (group: Group) =>
                    isStandaloneGroup(group) && !!group.gateways.length
            )
            .map(renameStandaloneGroup);
        setGroups(filteredGroups);

        const filteredGatewayIds = filteredGroups.flatMap((group) =>
            group.gateways.map(({ gateway_id }) => gateway_id)
        );
        setGatewayIds((gwIds) => uniq(gwIds.concat(filteredGatewayIds)));

        if (selectedGWs) {
            setSelectedIds(selectedGWs);
        }
    }, [isShow, initGroups, selectedGWs]);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        const searchingText = e.target.value;
        const groupsSearched: Group[] = initGroups
            .filter((group: Group) => isStandaloneGroup(group))
            .map((group: Group) =>
                renameStandaloneGroup(filterGateways(group, searchingText))
            )
            .filter((group: Group) => !!group.gateways.length);
        setGroups(groupsSearched);
    };

    const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedIds(gatewayIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedIds(selectedIds.concat(e.target.value));
        } else {
            setSelectedIds(
                selectedIds.filter((gwId) => gwId !== e.target.value)
            );
        }
    };

    const handleClose = () => {
        onClose();
    };

    const handleAddGatewaysToGroup = () => {
        handleAddGWsToGroup(selectedIds);
        handleClose();
    };

    const renderGroupGateways = () => {
        return groups.map(({ name, gateways, uuid }: Group) => {
            return (
                <div key={name + uuid}>
                    <h4 className="mb-0">{name}</h4>
                    {renderGateways(gateways)}
                </div>
            );
        });
    };

    const renderGateways = (gateways: Registry[]) => {
        return gateways
            .map(({ name, gateway_id }: Registry) => {
                return (
                    <div className="modal-option" key={gateway_id}>
                        <Form.Check
                            aria-label="select-gateway-checkbox"
                            name="selected-gateway"
                            id={gateway_id}
                            custom
                            type="checkbox"
                            value={gateway_id}
                            onChange={handleSelect}
                            checked={selectedIds.includes(gateway_id)}
                            label={
                                <div>
                                    <h4 className="select-gw-name">{name}</h4>{" "}
                                    <p className="select-gw-id">
                                        UUID: {gateway_id}
                                    </p>
                                </div>
                            }
                            className="float-left"
                        />
                    </div>
                );
            })
            .filter(Boolean);
    };

    return (
        <StyledModal show={isShow} animation={false} centered onHide={() => {}}>
            <Modal.Header>
                <Modal.Title>Select Gateway</Modal.Title>
                <Button
                    variant=""
                    className="close-button"
                    onClick={handleClose}
                >
                    <span className="material-icons">close</span>
                </Button>
            </Modal.Header>
            <Modal.Body>
                <Form className="d-flex flex-wrap">
                    <div className="d-flex align-items-center w-100 mb-4 modal-filter">
                        <div className="modal-option">
                            <Form.Check
                                id="gateway-check-all"
                                type="checkbox"
                                className="text-gray"
                                label="Select All"
                                custom
                                onChange={handleSelectAll}
                                checked={
                                    selectedIds.length === gatewayIds.length &&
                                    gatewayIds.length !== 0
                                }
                            />
                        </div>
                        <FormControl
                            aria-label="search-standalone-gateways"
                            type="text"
                            placeholder="Search"
                            onChange={handleSearch}
                        ></FormControl>
                    </div>

                    <div className="scroll-list float-left">
                        {renderGroupGateways()}
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddGatewaysToGroup}
                >
                    DONE
                </Button>
            </Modal.Footer>
        </StyledModal>
    );
};

export default SelectGatewayModal;
